import { delayAsync } from "@eusoft/webapp-core";;
import { IPitchFilter, IPitchValue } from "./abstraction/IPitchFilter";
import { PitchAlgo1 } from "./algo/PitchAlgo1";
import { PitchFilter1 } from "./filters/PitchFilter1";
import { IClock } from "../player/abstraction/IClock";
import { IPitchAlgo } from "./abstraction/IPitchAlgo";
import { PitchAlgo2 } from "./algo/PitchAlgo2";
import { MultiFilter } from "./filters/MultiFilter";
import { IMappedEventTarget } from "../Types";
import { OutOfRange } from "./filters/OutOfRange";
import { PeackRemove } from "./filters/PeackRemove";
import { PitchAlgo2Wasm } from "./algo/PitchAlgo2Wasm";

export interface IPitchDetectorOptions {
    maxFreq: number;
    minFreq: number;
    fftSize: number;
    clock: IClock;
    delayTime: number;
    algo: () => IPitchAlgo;
    filter: () => IPitchFilter;
}

type PitchDetectorEventMap = {
    "pitch": CustomEvent<IPitchValue>
}

export class PitchDetector extends EventTarget implements IMappedEventTarget<PitchDetectorEventMap> {

    protected _audioContext: AudioContext;
    protected _options: IPitchDetectorOptions;
    protected _state: "start" | "stop" | "pause";
    protected _stream: MediaStream;

    constructor(options?: IPitchDetectorOptions) {

        super();

        this._options = {
            maxFreq: 9000,
            minFreq: 100,
            fftSize: Math.pow(2, 10 + 3),
            delayTime: 1,
            clock: undefined,
            algo: () => new PitchAlgo2Wasm(),
            filter: () => new MultiFilter(new OutOfRange(), new PitchFilter1()),
            ...options
        }

        this._state = "stop";

        this.clock = this._options.clock;
    }

    pause() {

        if (this._state != "start")
            return;

        this._state = "pause";
    }

    stop() {

        if (this._state == "stop")
            return;

        this._state = "stop";

        this._audioContext.close();

        if (this._stream) {
            for (const track of this._stream.getTracks())
                track.stop();
            this._stream = undefined;
        }

        this._audioContext = undefined;
    }

    protected setState(value: typeof this._state) {
        this._state = value;
    }

    async startAsync(sourceOrId: MediaStream | HTMLAudioElement | string) {

        if (this._state == "start")
            return;

        if (this._state == "pause") {
            this._state = "start";
            return;
        }

        if (typeof sourceOrId == "string") {

            this._stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    deviceId: sourceOrId,
                    channelCount: 1,
                    autoGainControl: false,
                    echoCancellation: false
                }
            });
            sourceOrId = this._stream;
        }

        this._audioContext = new AudioContext();

        const analyser = this._audioContext.createAnalyser();

        const source = sourceOrId instanceof MediaStream ? this._audioContext.createMediaStreamSource(sourceOrId) : this._audioContext.createMediaElementSource(sourceOrId);
       
        source.connect(analyser);

        analyser.fftSize = this._options.fftSize;

        if (sourceOrId instanceof HTMLAudioElement)
            analyser.connect(this._audioContext.destination);

        const bufferLength = analyser.frequencyBinCount;

        this.timeData = new Float32Array(bufferLength);

        this.freqData = new Float32Array(bufferLength);

        const algo = this._options.algo();

        this.filter = this._options.filter();

        await algo.initAsync(this._audioContext.sampleRate, bufferLength);

        this.setState("start")

        console.log("detector start");

        while (this._state != "stop") {

            while (this._state == "pause")
                await delayAsync(1000);

            analyser.getFloatTimeDomainData(this.timeData);

            analyser.getFloatFrequencyData(this.freqData);

            const time = this.clock.getTime() - 0.5;

            const pitch = algo.getPicth(this.timeData, this.freqData);

            const index = this.getFreqIndex(pitch);

            const power = index < 0 ? 0 : this.freqData[index];

            const filterPitch = this.filter.processSample({
                pitch: pitch < 0 ? undefined : pitch,
                time: time,
                sampleTime: this._audioContext.currentTime,
                power
            });

            if (index > 0)
                this.lastFreqIndex = index;

            this.onPitch(filterPitch);

            await delayAsync(10);
        }

        console.log("detector stop");
    }

    protected onPitch(value: IPitchValue) {

        this.dispatchEvent(new CustomEvent("pitch", { detail: value }));
    }

    getFreqIndex(value: number) {

        if (!this._audioContext)
            return;

        const res = this._audioContext.sampleRate / this._options.fftSize;
        const index = Math.round(value / res);
        return index;
    }

    getIndexFreq(index: number) {

        if (!this._audioContext)
            return;

        return index / (this._options.fftSize * this._audioContext.sampleRate);
    }

    get options() {
        return this._options;
    }

    get state() {
        return this._state;
    }

    filter: IPitchFilter;

    lastFreqIndex: number;

    timeData: Float32Array;

    freqData: Float32Array;

    clock: IClock;
}